import React from 'react';
import { BotIcon, LinkIcon, MessageSquareIcon, ChevronRightIcon, ActivityIcon, ClockIcon, BrainIcon, ZapIcon, UsersIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge } from '../knowledge-space/chatbots/StatusBadge';
interface ChatbotOverviewProps {
  id: string;
  name: string;
  description: string;
  connectedSpaces: number[];
  totalInteractions: number;
  status: 'active' | 'inactive';
  type: 'assistant' | 'researcher' | 'analyst';
  lastActive: string;
  style?: React.CSSProperties;
  averageResponseTime?: string;
  successRate?: number;
  activeUsers?: number;
}
export const ChatbotOverviewCard = ({
  id,
  name,
  description,
  connectedSpaces,
  totalInteractions,
  status,
  type,
  lastActive,
  style,
  averageResponseTime = '1.2s',
  successRate = 98.5,
  activeUsers = 156
}: ChatbotOverviewProps) => {
  const navigate = useNavigate();
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigate(`space/${connectedSpaces[0]}/chatbot/${id}`);
    }
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const getTypeIcon = () => {
    switch (type) {
      case 'researcher':
        return <BrainIcon size={16} className="text-accent-light" data-id="element-299" />;
      case 'assistant':
        return <MessageSquareIcon size={16} className="text-accent-light" data-id="element-300" />;
      case 'analyst':
        return <ActivityIcon size={16} className="text-accent-light" data-id="element-301" />;
      default:
        return <BotIcon size={16} className="text-accent-light" data-id="element-302" />;
    }
  };
  return <button onClick={() => navigate(`space/${connectedSpaces[0]}/chatbot/${id}`)} onKeyPress={handleKeyPress} className="w-full text-left group bg-surface-light rounded-none border border-surface-border p-6 
                hover:bg-surface-mid transition-colors duration-150 cursor-pointer relative overflow-hidden 
                focus:outline-none focus:ring-1 focus:ring-accent focus:ring-offset-1 focus:ring-offset-surface" style={style} data-id="element-303">
      <div className="relative space-y-6" data-id="element-304">
        {/* Header */}
        <div className="flex items-start justify-between gap-3" data-id="element-305">
          <div className="flex-1 min-w-0" data-id="element-306">
            <div className="flex items-center gap-3 flex-wrap mb-2" data-id="element-307">
              <h3 className="text-lg font-medium text-white font-mono uppercase tracking-wide group-hover:text-accent-light transition-colors line-clamp-1" data-id="element-308">
                {name}
              </h3>
              <StatusBadge status={status} data-id="element-309" />
            </div>
            <p className="text-sm text-gray-400 line-clamp-2" data-id="element-310">{description}</p>
          </div>
          <ChevronRightIcon size={16} className="text-gray-500 group-hover:text-accent-light group-hover:translate-x-0.5 transition-all duration-150 shrink-0 mt-1" aria-hidden="true" data-id="element-311" />
        </div>
        {/* Type Info */}
        <div className="flex items-center gap-4" data-id="element-312">
          <div className="p-2 bg-surface rounded-none border border-surface-border group-hover:border-accent/30 transition-colors" data-id="element-313">
            {getTypeIcon()}
          </div>
          <div data-id="element-314">
            <div className="text-lg font-mono text-white capitalize" data-id="element-315">
              {type}
            </div>
            <div className="text-xs uppercase tracking-wider text-gray-400 font-mono" data-id="element-316">
              Chatbot Type
            </div>
          </div>
        </div>
        {/* Stats Grid */}
        <div className="grid grid-cols-3 gap-4 pt-4 border-t border-surface-border" data-id="element-317">
          <div className="space-y-1" data-id="element-318">
            <div className="flex items-center gap-2" data-id="element-319">
              <ZapIcon size={14} className="text-accent-light" data-id="element-320" />
              <span className="text-sm text-white font-mono" data-id="element-321">
                {averageResponseTime}
              </span>
            </div>
            <div className="text-xs text-gray-400 font-mono uppercase" data-id="element-322">
              Avg Response
            </div>
          </div>
          <div className="space-y-1" data-id="element-323">
            <div className="flex items-center gap-2" data-id="element-324">
              <ActivityIcon size={14} className="text-accent-light" data-id="element-325" />
              <span className="text-sm text-white font-mono" data-id="element-326">
                {successRate}%
              </span>
            </div>
            <div className="text-xs text-gray-400 font-mono uppercase" data-id="element-327">
              Success Rate
            </div>
          </div>
          <div className="space-y-1" data-id="element-328">
            <div className="flex items-center gap-2" data-id="element-329">
              <UsersIcon size={14} className="text-accent-light" data-id="element-330" />
              <span className="text-sm text-white font-mono" data-id="element-331">
                {activeUsers}
              </span>
            </div>
            <div className="text-xs text-gray-400 font-mono uppercase" data-id="element-332">
              Active Users
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 pt-4 border-t border-surface-border" data-id="element-333">
          <div className="flex items-center gap-4" data-id="element-334">
            <div className="flex items-center gap-2" data-id="element-335">
              <LinkIcon size={14} className="text-accent-light" data-id="element-336" />
              <span className="text-xs text-gray-400 font-mono uppercase" data-id="element-337">
                {connectedSpaces[0]} Space{connectedSpaces[0] !== 1 && 's'}
              </span>
            </div>
            <div className="flex items-center gap-2" data-id="element-338">
              <MessageSquareIcon size={14} className="text-accent-light" data-id="element-339" />
              <span className="text-xs text-gray-400 font-mono uppercase" data-id="element-340">
                {totalInteractions.toLocaleString()} Chats
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 text-gray-400" data-id="element-341">
            <ClockIcon size={14} data-id="element-342" />
            <span className="text-xs font-mono" data-id="element-343">
              Active {formatDate(lastActive)}
            </span>
          </div>
        </div>
      </div>
    </button>;
};